<template>
  <div class="flex flex-col max-sm:h-full">
    <!-- Desktop header -->
    <h3 class="max-sm:hidden text-3xl font-bold leading-6 text-color-1">
      {{ isUpdate ? $t('PaymentLinks | update') : $t('PaymentLinks | new') }}
    </h3>

    <!-- Mobile sticky header -->
    <div class="sm:hidden sticky-header-container">
      <h3 class="text-xl sm:text-3xl font-bold leading-6 text-color-1">
        {{ isUpdate ? $t('PaymentLinks | update') : $t('PaymentLinks | new') }}
      </h3>
    </div>

    <amount-input
      v-model="formData.data.amount"
      class="sm:mt-[29.5px] max-sm:mt-1"
      :label="amountInputLabel"
      :label-variant="'muted'"
      :show-popover="true"
      :popover-content="amountPopoverText"
      :show-flexible-amount-info="true"
      :flexible-amount="formData.payment_type"
      :min-amount="partnerSettings?.min_payment"
      :max-amount="partnerSettings?.max_payment"
      :coins-list="coins"
      :selected-coin-id="formData.data.coin_id"
      :has-error="hasAmountError"
      :is-one-time="formData.is_one_time"
      @update:coin="onCoinChange"
    />

    <div class="flex flex-col gap-y-2 mt-6">
      <div class="flex items-center justify-between py-1">
        <div class="flex flex-col gap-1">
          <div
            class="overflow-hidden text-color-1 text-ellipsis font-medium text-base leading-[150%]"
          >
            {{ $t('payment_link|form|label|flexible_amount') }}
          </div>
          <div
            class="text-color-4 font-medium text-sm leading-[150%] gap-2 inline-flex items-center"
          >
            <span>{{ flexibleAmountDescription }}</span>
            <base-popover
              class="inline-block ml-1.5 align-middle"
              placement="top"
              popover-type="tooltip"
              trigger="hover"
            >
              <template #trigger>
                <div class="cursor-pointer text-primary-main">
                  <svg-icon name="ic-info-tooltip" />
                </div>
              </template>
              <template #content>
                <div class="text-color-4 text-sm font-medium">
                  {{ $t('payment_link|form|tooltip|flexible_amount') }}
                </div>
              </template>
            </base-popover>
          </div>
        </div>
        <div class="flex items-center justify-center">
          <base-toggle
            v-model="formData.payment_type"
            @input="(event) => (formData.flexibleAmount = event)"
          />
        </div>
      </div>

      <div class="flex items-center justify-between py-1">
        <div class="flex flex-col gap-1">
          <div
            class="overflow-hidden text-color-1 text-ellipsis font-medium text-base leading-[150%]"
          >
            {{ $t('payment_link|form|label|auto_convert') }}
          </div>
          <div
            class="text-color-4 font-medium text-sm leading-[150%] gap-2 inline-flex items-center"
          >
            <span class="max-[495px]:max-w-[230px]">{{
              $t('payment_link|form|description|auto_convert')
            }}</span>
            <base-popover
              placement="top"
              popover-type="tooltip"
              trigger="hover"
            >
              <template #trigger>
                <div class="cursor-pointer text-primary-main">
                  <svg-icon name="ic-info-tooltip" />
                </div>
              </template>
              <template #content>
                <div class="text-color-4 text-sm font-medium">
                  {{ $t('payment_link|form|tooltip|auto_convert') }}
                </div>
              </template>
            </base-popover>
          </div>
        </div>
        <div class="flex items-center justify-center">
          <stable-popover
            v-if="!isUSDTSelected"
            placement="left"
            popover-type="tooltip"
            trigger="hover"
            background="#1a1a1a"
          >
            <template #trigger>
              <div :style="{ opacity: 0.5, cursor: 'not-allowed' }">
                <base-toggle
                  :model-value="formData.allow_different_currency"
                  :disabled="true"
                  @update:modelValue="onChangeAllowDifference"
                />
              </div>
            </template>
            <template #content>
              <div class="text-color-4 text-sm font-medium">
                {{ $t('payment_link|form|tooltip|auto_convert_disabled') }}
              </div>
            </template>
          </stable-popover>
          <base-toggle
            v-else
            :model-value="formData.allow_different_currency"
            @update:modelValue="onChangeAllowDifference"
          />
        </div>
      </div>

      <div class="flex items-center justify-between py-1">
        <div class="flex flex-col gap-1">
          <div
            class="overflow-hidden text-color-1 text-ellipsis font-medium text-base leading-[150%]"
          >
            {{ $t('payment_link|form|label|one_time') }}
          </div>
          <div
            class="text-color-4 font-medium text-sm leading-[150%] gap-2 inline-flex items-center"
          >
            <span class="max-[495px]:max-w-[230px]">{{
              $t('payment_link|form|description|one_time')
            }}</span>
            <base-popover
              placement="top"
              popover-type="tooltip"
              trigger="hover"
            >
              <template #trigger>
                <div class="cursor-pointer text-primary-main">
                  <svg-icon name="ic-info-tooltip" />
                </div>
              </template>
              <template #content>
                <div class="text-color-4 text-sm font-medium">
                  {{ $t('payment_link|form|tooltip|one_time') }}
                </div>
              </template>
            </base-popover>
          </div>
        </div>
        <div class="flex items-center justify-center">
          <base-toggle
            v-model="formData.is_one_time"
            @input="(event) => (formData.is_one_time = event)"
          />
        </div>
      </div>
    </div>

    <div class="my-10 h-[1px] bg-color-35"></div>

    <div>
      <div class="flex flex-col gap-y-4">
        <div class="flex justify-between items-center gap-x-4">
          <div class="flex items-center gap-x-1.5">
            <div class="text-base text-color-4 font-medium whitespace-nowrap">
              {{ $t('payment_link|form|label|payment') }}
            </div>

            <base-popover
              placement="right"
              popover-type="tooltip"
              trigger="hover"
            >
              <template #trigger>
                <div class="cursor-pointer text-primary-main">
                  <svg-icon name="ic-info-tooltip" />
                </div>
              </template>
              <template #content>
                <div class="text-color-4 text-sm font-medium">
                  {{ $t('payment_link|form|description|pay_tooltip') }}
                </div>
              </template>
            </base-popover>
          </div>

          <div
            class="flex-1 h-0.5 border-t border-dashed border-color-35"
          ></div>

          <div
            v-if="!formData.allow_different_currency"
            class="text-base text-color-1 flex gap-x-1.5 items-center"
          >
            <img
              v-if="selectedCoin?.media_url"
              :src="selectedCoin.media_url"
              :alt="selectedCoin.optionLabel"
              class="w-5 h-5 rounded-full"
            />
            <div>{{ selectedCoin?.optionLabel || 'USDT' }}</div>
          </div>

          <div v-else class="flex gap-x-1.5 items-center">
            <div class="flex items-center">
              <div
                v-for="(coin, index) in displayedCoins"
                :key="coin.optionValue"
                class="w-6 h-6 rounded-full border-2 border-white"
                :style="{
                  marginLeft: index > 0 ? '-8px' : '0',
                  zIndex: 10 - index,
                }"
              >
                <img
                  :src="coin.media_url"
                  :alt="coin.optionLabel"
                  class="w-full h-full rounded-full"
                />
              </div>
            </div>
            <stable-popover
              v-if="remainingCoinsCount > 0"
              placement="top-left"
              popover-type="tooltip"
              trigger="hover"
              mobile-placement="top-left"
            >
              <template #trigger>
                <span
                  class="text-base text-color-1 font-medium whitespace-nowrap flex items-center gap-x-1 cursor-pointer hover:text-brand transition-colors"
                >
                  {{
                    $t('payment_link|form|text|additional_coins', {
                      count: remainingCoinsCount,
                    })
                  }}
                </span>
              </template>
              <template #content>
                <div class="flex flex-col">
                  <div class="mb-1.5">
                    <search-input-new
                      v-model="coinSearchQuery"
                      :placeholder-dto="$t('Sidebar | searchSubAcc')"
                      background="bg-bg-2"
                      padding="p-2.5"
                      :show-close-icon="false"
                      :style="{ color: 'var(--text-4)' }"
                    />
                  </div>
                  <div class="flex flex-col w-80">
                    <div
                      v-for="coin in filteredCoins"
                      :key="coin.optionValue"
                      class="p-1.5"
                    >
                      <div class="flex item-center gap-x-4">
                        <div>
                          <img class="w-8 h-8" :src="coin.media_url" alt="" />
                        </div>

                        <div class="flex items-center gap-x-2">
                          <div class="text-sm text-color-1">
                            {{ coin.optionLabel }}
                          </div>
                          <div class="w-1.5 h-1.5 rounded-full bg-light"></div>
                          <div class="text-sm text-light">
                            {{ coin.description }}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </template>
            </stable-popover>
          </div>
        </div>

        <div class="flex justify-between items-center gap-x-4">
          <div class="flex items-center gap-x-1.5">
            <div class="text-base text-color-4 font-medium whitespace-nowrap">
              {{ $t('payment_link|form|label|to_credit') }}
            </div>

            <base-popover
              placement="top"
              popover-type="tooltip"
              trigger="hover"
            >
              <template #trigger>
                <div class="cursor-pointer text-primary-main">
                  <svg-icon name="ic-info-tooltip" />
                </div>
              </template>
              <template #content>
                <div class="text-color-4 text-sm font-medium">
                  {{ $t('payment_link|form|description|tooltip_enrollment') }}
                </div>
              </template>
            </base-popover>
          </div>

          <div
            class="flex-1 h-0.5 border-t border-dashed border-color-35"
          ></div>

          <div class="text-base text-color-1 flex gap-x-1.5 items-center">
            <div>{{ displayAmount }}</div>
            <div>{{ selectedCoin?.optionLabel || 'USDT' }}</div>
          </div>
        </div>

        <div class="flex items-center gap-x-4">
          <div class="flex items-center gap-x-1.5">
            <span class="text-base text-color-4 font-medium whitespace-nowrap">
              {{ $t('payment_link|form|label|fee') }}
            </span>

            <base-popover
              placement="top"
              popover-type="tooltip"
              trigger="hover"
            >
              <template #trigger>
                <div class="cursor-pointer text-primary-main">
                  <svg-icon name="ic-info-tooltip" />
                </div>
              </template>
              <template #content>
                <div class="text-color-4 text-sm font-medium">
                  {{
                    $t('payment_link|form|description|tooltip_fee', {
                      fee: feeText,
                      coinId: selectedCoin?.optionLabel || 'USDT',
                      minAmount: minAmountFormatted,
                    })
                  }}
                </div>
              </template>
            </base-popover>
          </div>
          <div
            class="flex-1 h-0.5 border-t border-dashed border-color-35"
          ></div>
          <span
            class="text-base text-color-1 font-medium md:whitespace-nowrap text-right md:text-left"
          >
            {{ feeText }}
          </span>
        </div>

        <div class="flex items-center gap-x-4">
          <span class="text-base text-color-4 font-medium whitespace-nowrap">
            {{ $t('payment_link|form|label|crediting_time') }}
          </span>
          <div
            class="flex-1 h-0.5 border-t border-dashed border-color-35"
          ></div>
          <span
            class="text-base text-color-1 font-medium md:whitespace-nowrap text-right md:text-left flex items-center gap-x-1"
          >
            {{ $t('payment_link|form|text|crediting_time') }}
          </span>
        </div>

        <div class="flex items-center gap-x-4">
          <span class="text-base text-color-4 font-medium whitespace-nowrap">
            {{ $t('payment_link|form|label|payment_period') }}
          </span>
          <div
            class="flex-1 h-0.5 border-t border-dashed border-color-35"
          ></div>
          <span
            class="text-base text-color-1 font-medium md:whitespace-nowrap text-right md:text-left"
            v-html="
              $t('payment_link|form|text|period_deadline', {
                deadline: formattedDeadline,
              })
            "
          ></span>
        </div>
      </div>
    </div>

    <!-- Desktop button -->
    <div class="max-sm:hidden !w-full">
      <base-button
        class="mt-[30px] !w-full"
        :disabled="disableButton"
        @onClick="onSubmit"
      >
        <base-loader v-if="loading" :stroke-width="3" />

        <div v-else>
          {{ $t(buttonName) }}
        </div>
      </base-button>
    </div>

    <!-- Mobile sticky button -->
    <div class="sm:hidden sticky-button-container">
      <base-button
        class="!w-full"
        :disabled="disableButton"
        @onClick="onSubmit"
      >
        <base-loader v-if="loading" :stroke-width="3" />

        <div v-else>
          {{ $t(buttonName) }}
        </div>
      </base-button>
    </div>
  </div>
</template>

<script setup>
import { reactive, computed, watch, ref } from 'vue'
import { useStore } from 'vuex'
import { useI18n } from 'vue-i18n'
import { BasePopover } from '@emcd-shared/base-popover'
import BaseToggle from '@emcd-shared/base-toggle'
import BaseLoader from '@/packages/BaseLoader/Spinner/Spinner.vue'
import BaseButton from '@/shared/ui/BaseButton/BaseButton.vue'

import AmountInput from '@/shared/ui/AmountInput/ui/index.vue'
import SvgIcon from '@/shared/ui/SvgIcon/SvgIcon.vue'
import StablePopover from '@/shared/ui/StablePopover/StablePopover.vue'
import { usePartnerFees } from '@/shared/composables/usePartnerFees'

import { PAYMENT_LINK_GETTERS } from '@/shared/constants/store-constants/paymentLink'
import {
  PAYMENT_LINK_PERIOD_PAYMENTS,
  PAYMENT_LINK_TYPES,
} from '@/shared/constants/paymentLink'
import { APP_GETTERS } from '@/shared/constants/store-constants/app'
import {
  PARTNER_SETTINGS_DISPATCHERS,
  PARTNER_SETTINGS_GETTERS,
} from '@/shared/constants/store-constants/partnerSettings'
import SearchInputNew from '@/shared/ui/SearchInput/SearchInputNew.vue'

const props = defineProps({
  item: {
    type: Object,
    default: () => ({}),
  },
  isUpdate: {
    type: Boolean,
    default: false,
  },
  show: {
    type: Boolean,
    default: false,
  },
})

const emits = defineEmits(['onSubmit'])

const store = useStore()
const { t, locale } = useI18n()
const { lowerFee } = usePartnerFees()

const coinSearchQuery = ref('')

const partnerSettings = computed(
  () => store.getters[PARTNER_SETTINGS_GETTERS.settings]
)

const disableButton = computed(() => {
  if (!formData.data.amount) {
    return true
  }

  const amount = Number(formData.data.amount)
  const minPayment = Number(partnerSettings.value?.min_payment)
  const maxPayment = Number(partnerSettings.value?.max_payment)

  // Если payment_type = false (фиксированная сумма)
  if (!formData.payment_type) {
    return amount < minPayment || amount > maxPayment
  }

  // Если payment_type = true (гибкая сумма)
  // amount - это максимум (До), минимум берется из настроек
  return amount < minPayment || amount > maxPayment
})

const formData = reactive({
  checkout_url: '',
  data: {
    title: t('PaymentLinks | defaultTitle'),
    description: t('PaymentLinks | defaultDescription'),
    coin_id: null,
    network_id: null,
    amount: partnerSettings.value ? partnerSettings.value.min_payment : 1,
    amount_min: null,
    amount_max: null,
  },
  amount_boundaries: {
    max_amount: null,
    min_amount: null,
  },
  expiration: {
    ui_type: PAYMENT_LINK_PERIOD_PAYMENTS.MONTH,
    deadline: null,
  },
  is_one_time: PAYMENT_LINK_TYPES.REUSABLE,
  payment_type: false,
  flexibleAmount: false,
  allow_different_currency: false,
})

const buttonName = computed(() =>
  props.isUpdate
    ? 'PaymentLinks | updateAndGetLink'
    : 'PaymentLinks | createAndGetLink'
)

const storeCoins = computed(() => store.getters[APP_GETTERS.coins])

const coins = computed(() =>
  storeCoins.value.map((item) => {
    return {
      optionValue: item.id,
      optionLabel: item.title,
      description: item.description,
      media_url: item.media_url,
      networks: item.networks,
    }
  })
)

const loading = computed(() => store.getters[PAYMENT_LINK_GETTERS.isLoading])

const displayedCoins = computed(() => {
  const sortedCoins = [...coins.value]
  const usdtIndex = sortedCoins.findIndex(
    (coin) => coin.optionLabel.toUpperCase() === 'USDT'
  )

  if (usdtIndex > 0) {
    const [usdt] = sortedCoins.splice(usdtIndex, 1)
    sortedCoins.unshift(usdt)
  }

  return sortedCoins.slice(0, 4)
})

const remainingCoinsCount = computed(() => {
  const total = coins.value.length
  return total > 4 ? total - 4 : 0
})

const flexibleAmountDescription = computed(() => {
  if (formData.payment_type && partnerSettings.value) {
    const coinId = (formData.data.coin_id || 'USDT').toUpperCase()
    const decimals = coinId === 'USDT' ? 2 : 6
    const min = Number(partnerSettings.value.min_payment).toFixed(decimals)
    const max = Number(partnerSettings.value.max_payment).toFixed(decimals)
    return `${min}-${max} ${coinId}`
  }
  return t('payment_link|form|description|amount_limit')
})

const amountInputLabel = computed(() => {
  if (formData.payment_type) {
    return t('payment_link|form|label|maximum_amount')
  }
  return t('payment_link|form|label|credited_amount')
})

const amountPopoverText = computed(() => {
  if (formData.payment_type) {
    const coinId = (formData.data.coin_id || 'USDT').toUpperCase()
    const decimals = coinId === 'USDT' ? 2 : 6
    const max = Number(partnerSettings.value.max_payment).toFixed(decimals)
    return t('payment_link|form|description|amount_limit_tooltip', {
      amount: max,
      coinId: coinId,
    })
  }
  return t('payment_link|form|description|credited_amount_tooltip')
})

const formattedDeadline = computed(() => {
  if (!formData.expiration.ui_type) return ''

  const deadline = createDeadlineDate()
  const formatter = new Intl.DateTimeFormat(locale.value, {
    day: 'numeric',
    month: 'long',
  })
  const formattedDate = formatter.format(deadline)

  return t('until | date', { date: formattedDate })
})

const feeText = computed(() => {
  const partnerSettings = store.getters[PARTNER_SETTINGS_GETTERS.settings]

  if (!lowerFee.value || !partnerSettings?.min_payment) {
    return '—'
  }

  return `${lowerFee.value * 100} %`
})

const hasAmountError = computed(() => {
  if (!formData.data.amount || !partnerSettings.value?.min_payment) {
    return false
  }

  const amount = Number(formData.data.amount)
  const minPayment = Number(partnerSettings.value.min_payment)
  const maxPayment = Number(partnerSettings.value.max_payment)

  return amount < minPayment || amount > maxPayment
})

const selectedCoin = computed(() => {
  return coins.value.find((coin) => coin.optionValue === formData.data.coin_id)
})

const displayAmount = computed(() => {
  if (!formData.data.amount) return '0.00'
  const amount = Number(formData.data.amount)
  const coinId = selectedCoin.value?.optionLabel?.toUpperCase() || 'USDT'
  const decimals = coinId === 'USDT' ? 2 : 6
  return amount.toFixed(decimals)
})

const isUSDTSelected = computed(() => {
  const coinLabel = selectedCoin.value?.optionLabel?.toUpperCase()
  return coinLabel === 'USDT'
})

const minAmountFormatted = computed(() => {
  if (!partnerSettings.value?.min_payment) return '0.00'
  const minAmount = Number(partnerSettings.value.min_payment)
  const coinId = selectedCoin.value?.optionLabel?.toUpperCase() || 'USDT'
  const decimals = coinId === 'USDT' ? 2 : 6
  return minAmount.toFixed(decimals)
})

const filteredCoins = computed(() => {
  if (!coinSearchQuery.value) {
    return coins.value
  }

  const query = coinSearchQuery.value.toLowerCase()
  return coins.value.filter((coin) => {
    const labelMatch = coin.optionLabel?.toLowerCase().includes(query)
    const descriptionMatch = coin.description?.toLowerCase().includes(query)
    return labelMatch || descriptionMatch
  })
})

function onCoinChange(coinId) {
  formData.data.coin_id = coinId
}

async function onSubmit() {
  if (loading.value) return

  formData.expiration.deadline = createDeadlineDate()

  // Если гибкая сумма (payment_type = true)
  if (formData.payment_type) {
    // Введенная сумма становится максимумом
    formData.data.amount_max = formData.data.amount
    formData.data.amount_min = partnerSettings.value.min_payment
    formData.data.amount = null

    formData.amount_boundaries.max_amount = formData.data.amount_max
    formData.amount_boundaries.min_amount = formData.data.amount_min
  } else {
    // Фиксированная сумма
    formData.data.amount_min = null
    formData.data.amount_max = null
    formData.amount_boundaries.max_amount = null
    formData.amount_boundaries.min_amount = null
  }

  emits('onSubmit', formData)
}

function onChangeAllowDifference(value) {
  console.log('thiss???', isUSDTSelected.value)
  if (!isUSDTSelected.value) {
    return
  }

  formData.allow_different_currency = value
}

function parseData() {
  if (props.isUpdate) {
    Object.keys(props.item).forEach((key) => {
      formData[key] = JSON.parse(JSON.stringify(props.item[key]))
    })
  } else {
    // Устанавливаем дефолтные значения с актуальным переводом
    formData.data.title = t('PaymentLinks | defaultTitle')
    formData.data.description = t('PaymentLinks | defaultDescription')

    if (coins.value.length > 0 && !formData.data.coin_id) {
      formData.data.coin_id = coins.value[0].optionValue
    }
  }

  if (formData.data.coin_id) {
    store.dispatch(
      PARTNER_SETTINGS_DISPATCHERS.getPartnerSettings,
      formData.data.coin_id
    )
  }
}

function createDeadlineDate() {
  const date = new Date()
  switch (formData.expiration.ui_type) {
    case PAYMENT_LINK_PERIOD_PAYMENTS.ONE_DAY:
      date.setDate(date.getDate() + 1)
      return date
    case PAYMENT_LINK_PERIOD_PAYMENTS.WEEK:
      date.setDate(date.getDate() + 7)
      return date
    case PAYMENT_LINK_PERIOD_PAYMENTS.TWO_WEEK:
      date.setDate(date.getDate() + 14)
      return date
    case PAYMENT_LINK_PERIOD_PAYMENTS.MONTH:
      date.setDate(date.getDate() + 30)
      return date
  }
}

function clearData() {
  Object.keys(formData.data).forEach((key) => {
    formData.data[key] = null
  })

  formData.expiration.deadline = null
  formData.expiration.ui_type = PAYMENT_LINK_PERIOD_PAYMENTS.MONTH

  formData.data.title = t('PaymentLinks | defaultTitle')
  formData.data.description = t('PaymentLinks | defaultDescription')
  formData.data.coin_id = coins.value[0].optionValue

  formData.data.amount = partnerSettings.value
    ? partnerSettings.value.min_payment
    : 1

  formData.payment_type = false
  formData.allow_different_currency = false
}

watch(
  () => props.show,
  (value) => {
    if (value) {
      parseData()
    } else {
      clearData()
    }
  },
  { immediate: true }
)

watch(
  partnerSettings,
  (value) => {
    if (!formData.payment_type) {
      // Фиксированная сумма
      formData.data.amount = value.min_payment
    } else {
      // Гибкая сумма - устанавливаем максимум
      formData.data.amount = value.max_payment
    }
  },
  { deep: true }
)

watch(
  () => formData.data.coin_id,
  () => {
    store.dispatch(
      PARTNER_SETTINGS_DISPATCHERS.getPartnerSettings,
      formData.data.coin_id
    )
  },
  { deep: true }
)

watch(isUSDTSelected, (newValue) => {
  // Если выбранная монета не USDT и тоглер был включен, выключаем его
  if (!newValue && formData.allow_different_currency) {
    formData.allow_different_currency = false
  }
})
</script>

<style scoped>
:deep(.content.tooltip) {
  white-space: pre-wrap;
}

@media (max-width: 639px) {
  .sticky-header-container {
    display: flex;
    padding: 16px;
    align-items: flex-start;
    align-self: stretch;
    position: sticky;
    top: -20px;
    background: var(--bg-2);
    margin-left: -20px;
    margin-right: -20px;
    margin-top: -20px;
    z-index: 10;
  }

  .sticky-button-container {
    display: flex;
    padding: 40px 20px;
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
    align-self: stretch;
    position: sticky;
    bottom: -20px;
    background: var(--bg-2);
    margin-left: -20px;
    margin-right: -20px;
    margin-top: 20px;
    border-top: 1px solid rgba(208, 208, 208, 0.07);
    z-index: 10;
  }
}
</style>
